import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import type { UIAnalyticsEventHandler } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { browserMetricsState } from '@confluence/browser-metrics/entry-points/utils.ts';
import { WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { ScrollToPageStart } from '@confluence/scroll/entry-points/ScrollToPageStart.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	TASKS_HOME_LOAD_KEY,
	CONFLUENCE_DRAFTS_LOAD_KEY,
	CONFLUENCE_RECENT_LOAD_KEY,
	CONFLUENCE_STARRED_LOAD_KEY,
} from '@confluence/browser-metrics/entry-points/metric-keys.ts';

import { CONFLUENCE_HOME_PAGE_LOAD } from '../perf.config.ts';

import { HomeNavigation, PersistentInviteButton } from './HomeNavigation/index.ts';
import { HomeMainContentArea } from './HomeMainContentArea.tsx';
import { Error } from './HomeViews/index.ts';
import { useHomeReadyForUser } from './useHomeReadyForUser.ts';
import { useHomeViewedAnalytics } from './useHomeViewedAnalytics.ts';
import { useRightSidebarCompensation } from './useRightSidebarCompensation.ts';
import { NAV_COLLAPSE_BREAKPOINT_PX, SINGLE_COLUMN_BREAKPOINT_PX } from './layoutConstants.ts';
import { useSelectedView } from './useSelectedView.ts';
import { View } from './homeRouteParams.ts';

const pageLoadMetricLookup = {
	[View.OVERVIEW]: undefined, //this is the default
	[View.DRAFTS]: CONFLUENCE_DRAFTS_LOAD_KEY,
	[View.RECENT]: CONFLUENCE_RECENT_LOAD_KEY,
	[View.STARRED]: CONFLUENCE_STARRED_LOAD_KEY,
	[View.TASKS]: TASKS_HOME_LOAD_KEY,
};

const i18n = defineMessages({
	homeH1Text: {
		id: 'home.h1.text',
		defaultMessage: 'Confluence Home',
		description: 'H1 heading text for the home page - visible to screen readers only',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const HomeContainer = styled.div`
	display: flex;
	justify-content: center;
	background-color: ${token('elevation.surface')};
	width: 100%;
	min-height: calc(100vh - var(--bannerHeight, 0px) - var(--topNavigationHeight, 0px));
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const HomeLayout = styled.div<{ rightSidebarCompensation: number; isNav4Enabled: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isNav4Enabled, rightSidebarCompensation }) => ({
		display: 'flex',
		flexBasis: '100%',
		margin: token('space.400'),
		maxWidth: '1440px',
		minWidth: '400px',
		'--homeIsSingleColumn': 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--homeNavIsCollapsed': isNav4Enabled ? 1 : 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--homeNavCollapsedDisplay': isNav4Enabled ? 'none' : 'block',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		justifyContent: isNav4Enabled ? 'center' : undefined,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		[`@media (max-width: ${SINGLE_COLUMN_BREAKPOINT_PX + rightSidebarCompensation}px)`]: {
			'--homeIsSingleColumn': 1,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		[`@media (max-width: ${NAV_COLLAPSE_BREAKPOINT_PX + rightSidebarCompensation}px)`]: {
			'--homeNavIsCollapsed': 1,
			'--homeNavCollapsedDisplay': 'none',
		},
	}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const HomeNavigationContainer = styled.div`
	max-width: 240px;
	display: flex;
	flex-direction: column;
	position: sticky;
	top: calc(var(--bannerHeight, 0px) + var(--topNavigationHeight, 0px) + 24px);
	height: calc(100vh - var(--bannerHeight, 0px) - var(--topNavigationHeight, 0px) - 40px);
	min-width: calc(240px - 196px * var(--homeNavIsCollapsed));
	max-height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: calc(${token('space.250')} - 10px * var(--homeNavIsCollapsed));
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const ContentContainer = styled.div`
	display: flex;
	flex-basis: 100%;
	max-width: 1152px;
	min-width: 516px;
	flex-grow: 1;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScreenReaderOnlyHeading = styled.h1({
	border: 0,
	clip: 'rect(1px, 1px, 1px, 1px)',
	'clip-path': 'inset(50%)',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	margin: '-1px',
	overflow: 'hidden',
	padding: '0',
	position: 'absolute',
	width: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	'word-wrap': 'normal !important',
});

const processAnalyticsEvents: UIAnalyticsEventHandler = (event) => {
	event.update((payload) => {
		return {
			...payload,
			data: {
				...payload.data,
				attributes: {
					...payload.data.attributes,
				},
			},
		};
	});
};

export const Home = () => {
	const intl = useIntl();
	const homeReadyForUser = useHomeReadyForUser();
	const rightSidebarCompensation = useRightSidebarCompensation();
	const isNav4Enabled = useIsNav4Enabled();
	const selectedView = useSelectedView();

	const { overrideUFORouteName } = useRouteActions();

	useEffect(() => {
		//set ufo page name correctly for each home route
		const pageLoadKey = pageLoadMetricLookup[selectedView];
		if (pageLoadKey) {
			overrideUFORouteName(pageLoadKey);
		}
	}, [overrideUFORouteName, selectedView]);

	useHomeViewedAnalytics();

	return (
		<AnalyticsListener onEvent={processAnalyticsEvents}>
			<ScrollToPageStart isSmooth={false} />
			<ScreenReaderOnlyHeading data-vc="screen-reader-home-h1-text">
				{intl.formatMessage(i18n.homeH1Text)}
			</ScreenReaderOnlyHeading>
			<GenericErrorBoundary attribution={Attribution.DISCO} renderOnError={Error}>
				<HomeContainer data-testid="home" data-vc="home-container">
					<HomeLayout
						rightSidebarCompensation={rightSidebarCompensation}
						isNav4Enabled={isNav4Enabled}
						data-testid="home-layout"
					>
						{!isNav4Enabled && (
							<HomeNavigationContainer>
								<HomeNavigation />
								{/* Add persistent button at the bottom of left navigation */}
								<PersistentInviteButton rightSidebarCompensation={rightSidebarCompensation} />
							</HomeNavigationContainer>
						)}
						<ContentContainer>
							<GenericErrorBoundary attribution={Attribution.DISCO} renderOnError={Error}>
								<HomeMainContentArea />
							</GenericErrorBoundary>
						</ContentContainer>
					</HomeLayout>
				</HomeContainer>
			</GenericErrorBoundary>
			{homeReadyForUser && (
				<PageLoadEnd
					metric={CONFLUENCE_HOME_PAGE_LOAD}
					customData={{
						initiallyWikiRoute: browserMetricsState.previousRoute === WIKI.name,
					}}
					// Set TTI = FMP ( = FCP) when SSR
					stopTime={window.__SSR_RENDERED__ ? getMark('CFP-63.ssr-ttr') : undefined}
				/>
			)}
		</AnalyticsListener>
	);
};
